import React, { FC, useCallback, useMemo, useState } from 'react';
import {
	removeBuyerSubscriberForBuyers,
	removeSupplierSubscriberForSuppliers,
} from '../../../thunks/work_orders_subscribers_thunks';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { withUserType } from '../../withUserType';
import { nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { getSubscribeObjectForWorkOrder } from '../../../utils/NotificationUtils';
import { message } from 'antd';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../../actions/work_orders_actions';
import WorkOrderNotificationActionCard from './WorkOrderNotificationActionCard';
import WorkOrderNotificationSuccessCard from './WorkOrderNotificationSuccessCard';

const WorkOrderNotificationsUnsubscribePage: FC<any> = ({
	workOrder,
	userType,
	currentUser,
	match,
	unsubscribeFromWO,
	history,
	updateWorkOrderInRedux,
}): React.ReactElement => {
	const [saving, setSaving] = useState(false);

	const workOrderId = match.params.id;

	const subscriberList = useMemo(() => {
		return userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse('supplierSubscriberEmails', workOrder, [])
			: nullSafeGetOrElse('buyerSubscriberEmails', workOrder, []);
	}, [workOrder, userType]);

	const isSubscribed = useMemo(
		() => subscriberList.includes(nullSafeGetOrElse('email', currentUser, '')),
		[currentUser, subscriberList]
	);

	const onUnSubscribe = useCallback(() => {
		setSaving(true);
		const subscribeObject = getSubscribeObjectForWorkOrder(isSubscribed, workOrderId, currentUser);
		unsubscribeFromWO(subscribeObject, workOrder)
			.then((data) => {
				updateWorkOrderInRedux(data);
				message.success('Unsubscribed successfully!');
			})
			.catch(message.error)
			.finally(() => setSaving(false));
	}, [
		currentUser,
		isSubscribed,
		unsubscribeFromWO,
		updateWorkOrderInRedux,
		workOrder,
		workOrderId,
	]);

	const onCancel = useCallback(() => {
		history.replace('/');
	}, [history]);

	const onRestoreSubscription = useCallback(() => {
		history.replace(`/${userType}/workorder_notifications/${workOrderId}/subscribe`);
	}, [history, userType, workOrderId]);

	if (!nullSafeGet('id', workOrder)) {
		history.replace(`/${userType}/settings/notifications`);
	}

	return isSubscribed ? (
		<WorkOrderNotificationActionCard
			workOrderId={workOrderId}
			saving={saving}
			onConfirm={onUnSubscribe}
			onCancel={onCancel}
		/>
	) : (
		<WorkOrderNotificationSuccessCard
			workOrderId={workOrderId}
			successTitle="You successfully unsubscribed"
			revertElement={
				<>
					We’ll no longer send email notifications or push notifications for{' '}
					<span className="font-semibold">{`WO #${workOrderId}`}</span>
				</>
			}
			onReverseAction={onRestoreSubscription}
		/>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	workOrder: state.work_orders.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	unsubscribeFromWO: (obj, workOrder) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? removeSupplierSubscriberForSuppliers(obj)(workOrder)
				: removeBuyerSubscriberForBuyers(obj)(workOrder)
		),
	updateWorkOrderInRedux: (workOrder) => {
		dispatch(WORK_ORDERS_CRUD_ACTION_CREATORS.updateSuccess(workOrder));
	},
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(WorkOrderNotificationsUnsubscribePage))
);
